'use client';

import { isAllCasesSelection } from '@/shared/case-summary';
import { useCaseFolderName, useCaseFolderNameResolver } from './useCaseFolderName';
import { useCasesList } from './useCasesList';

/**
 * The value provenance's content-moderation endpoints want as ``case_id``: the
 * case's **S3 folder segment**, not the display name the nav dropdown holds.
 *
 * A finding's ``case_id`` is written by data-processing from the ingest tag —
 * ``_extract_case_name`` returns ``tag[len("case:"):]`` — so it is the folder
 * segment, which is a ``folder_token`` (UUID) for any case that has one. The
 * frontend's ``caseId`` is the nav selection, which is built purely from
 * ``caseName`` (``use-primary-nav-cases.ts``). Those two are the same string for
 * a legacy case and only for a legacy case, which is why passing the display
 * name worked for years and breaks the moment a tenant mints folder tokens
 * (``caseCreationMode`` AUTO / GATED).
 *
 * The failure is quiet and asymmetric, which is why this is centralised rather
 * than fixed per call site:
 *   - an access-enforced tenant 404s (``assert_case_access`` builds
 *     ``case:<case_id>`` and compares against the caller's accessible tags);
 *   - an allow-all tenant skips that check entirely and returns an empty list,
 *     so the CSAM dashboard reads as "nothing flagged" on a case full of
 *     findings, and the nav badge reads zero.
 *
 * Provenance lowercases ``case_id`` on every content-moderation path, so the
 * segment's original casing is safe to send as-is.
 *
 * Returns ``null`` until the cases list resolves. Callers MUST treat that as
 * "not ready" — keep the query disabled, or refuse the mutation — and must not
 * fall back to ``caseId``: that is the bug this exists to prevent.
 */
export function useContentModerationFolderName(
  caseId: string | null | undefined,
): string | null {
  return useContentModerationCase(caseId).folderName;
}

export type ContentModerationCase = {
  /** The folder segment to send as ``case_id``, or ``null`` when not resolved. */
  folderName: string | null;
  /**
   * True while the segment is still *coming* — distinct from resolved-to-nothing.
   *
   * Gating a query on ``Boolean(folderName)`` is necessary but not sufficient: a
   * disabled TanStack query reports ``isLoading === false`` with ``data ===
   * undefined``, so a consumer reading ``isLoading`` sees a *settled empty
   * result* while the cases list is still in flight. On the CSAM dashboard that
   * rendered "no flagged sources" and computed "fully reviewed" for a case full
   * of findings — the same silent-empty failure this module exists to prevent.
   *
   * Consumers must fold this into whatever they treat as "loading".
   *
   * Goes false once the list lands (or errors) even if the case is still
   * unresolvable, so an absent or inaccessible case settles instead of spinning
   * forever.
   */
  isResolving: boolean;
};

/**
 * {@link useContentModerationFolderName} plus whether the segment is still
 * resolving. Prefer this wherever the answer drives a loading or empty state.
 */
export function useContentModerationCase(
  caseId: string | null | undefined,
): ContentModerationCase {
  const casesQuery = useCasesList();
  const folderName = useCaseFolderName(caseId);
  const wantsCase = Boolean(caseId?.trim()) && !isAllCasesSelection(caseId);
  // ``data === undefined`` is the only honest "not yet" signal: it stays defined
  // through a background refetch, so this does not flap on revalidation.
  const isResolving =
    wantsCase && !folderName && casesQuery.data === undefined && !casesQuery.isError;
  return { folderName, isResolving };
}

/**
 * {@link useContentModerationFolderName} as a callback, for mutations that
 * receive the case as a variable at call time rather than at render.
 */
export function useContentModerationFolderNameResolver(): (
  caseId: string | null | undefined,
) => string | null {
  return useCaseFolderNameResolver();
}
